/**
 * Passerelle MoMent → BoxyLab — la page Nafis parle à l'extension « Nafis Bridge ».
 *
 * L'extension se signale par `window.postMessage` (protocole : nafis-bridge-protocole.ts),
 * avec son identifiant ; la page ouvre alors un port vers elle (`externally_connectable`,
 * réservé à https://nafis.bio24.tn). Une demande = un numéro ; l'extension répond par des
 * messages `progres` puis UN message `fin`.
 *
 * Module NAVIGATEUR (window, chrome) : pas de test pur ici, la logique est dans
 * boxylab-identite.ts et boxylab-reponses.ts.
 */
import { ANNONCE, PORT, type Demande, type Message, type Progres } from './nafis-bridge-protocole';

type PortPage = {
  postMessage: (m: unknown) => void;
  disconnect: () => void;
  onMessage: { addListener: (f: (m: unknown) => void) => void };
  onDisconnect: { addListener: (f: () => void) => void };
};
type RuntimePage = { connect?: (id: string, info: { name: string }) => PortPage; lastError?: { message?: string } };

export type Extension = { id: string; version: string };

/** L'extension est-elle installée ? Elle répond à « qui est là ? » en moins d'une seconde et demie. */
export function trouverExtension(delaiMs = 1500): Promise<Extension | null> {
  return new Promise((resoudre) => {
    let fini = false;
    const ecoute = (e: MessageEvent) => {
      if (e.source !== window || e.origin !== window.location.origin) return;
      const m = e.data as { type?: string; id?: unknown; version?: unknown } | null;
      if (m?.type !== ANNONCE || typeof m.id !== 'string' || !/^[a-p]{32}$/.test(m.id)) return;
      fini = true;
      window.removeEventListener('message', ecoute);
      resoudre({ id: m.id, version: String(m.version || '') });
    };
    window.addEventListener('message', ecoute);
    window.postMessage({ type: `${ANNONCE}?` }, window.location.origin);
    setTimeout(() => { if (!fini) { window.removeEventListener('message', ecoute); resoudre(null); } }, delaiMs);
  });
}

type SansId<T> = T extends { id: number } ? Omit<T, 'id'> : never;

/** Un port ouvert vers l'extension, le temps d'un envoi. */
export class Pont {
  private port: PortPage;
  private suivant = 1;
  private attentes = new Map<number, { resoudre: (r: unknown) => void; rejeter: (e: Error) => void; progres?: (p: Progres) => void }>();
  private coupe = false;

  constructor(ext: Extension) {
    const runtime = (globalThis as unknown as { chrome?: { runtime?: RuntimePage } }).chrome?.runtime;
    if (!runtime?.connect) throw new Error('Ce navigateur ne permet pas de joindre l’extension Nafis Bridge (Chrome requis).');
    this.port = runtime.connect(ext.id, { name: PORT });
    this.port.onDisconnect.addListener(() => {
      this.coupe = true;
      for (const a of this.attentes.values()) a.rejeter(new Error('L’extension Nafis Bridge a coupé la liaison (rechargée, ou désinstallée ?).'));
      this.attentes.clear();
    });
    this.port.onMessage.addListener((brut) => {
      const m = brut as Message;
      const a = this.attentes.get(m?.id);
      if (!a) return;
      if (m.type === 'progres') { a.progres?.(m.progres); return; }
      this.attentes.delete(m.id);
      if (m.ok) a.resoudre(m.resultat);
      else a.rejeter(Object.assign(new Error(m.erreur), { etape: m.etape ?? null }));
    });
  }

  demander<T>(d: SansId<Demande>, progres?: (p: Progres) => void): Promise<T> {
    if (this.coupe) return Promise.reject(new Error('La liaison avec l’extension est coupée : rechargez la page.'));
    const id = this.suivant++;
    return new Promise<T>((resoudre, rejeter) => {
      this.attentes.set(id, { resoudre: resoudre as (r: unknown) => void, rejeter, progres });
      this.port.postMessage({ ...d, id });
    });
  }

  fermer() { try { this.port.disconnect(); } catch { /* déjà fermé */ } }
}

/** Le PDF figé (servi par le moteur pour CET envoi), en base64 pour traverser la messagerie de Chrome. */
export async function pdfEnBase64(url: string): Promise<string> {
  const r = await fetch(url, { credentials: 'include', cache: 'no-store' });
  if (!r.ok) {
    const j = await r.json().catch(() => null) as { error?: { message?: string } } | null;
    throw new Error(j?.error?.message || `Le PDF du CR n’a pas pu être lu (HTTP ${r.status}).`);
  }
  const octets = new Uint8Array(await r.arrayBuffer());
  let bin = '';
  for (let i = 0; i < octets.length; i += 0x8000) bin += String.fromCharCode(...octets.subarray(i, i + 0x8000));
  return btoa(bin);
}
